import React from 'react';

import { GridFilterOperator } from '@mui/x-data-grid';

import { DateTimeValue, formatFloating, formatMoment } from 'utils/formatters/timezone';

import { LocationAnswerType, UuidableName } from 'utils/api/types';

import { JSONSchemaType, JSONSchemaValue } from 'utils/api/modules/incidents/incidents.types';

import API from 'utils/api';

import { DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, EntityModules, ProtectorType } from 'core/constants';

import { timezoneStore } from 'stores/Timezone';

import { getRiskBand, isRated, RiskMatrixDisplay } from 'components/RiskMatrix';

import type { RiskMatrixValue } from 'components/RiskMatrix';

import Chip from 'components/Chip';

import { colors } from 'theme/colors';

import { FilterType, GridProtectorType, StringKeys } from './types';

import GridUserRenderer from './renderers/GridUserRenderer';
import textOperators from './operators/Text';
import BasicDateOperators, { ExtendedDateOperators, RangeOperators } from './operators/Date/Date';

import userOperators from './operators/User';
import { Text } from './styles';
import GridLookupRenderer from './renderers/GridLookupRenderer';
import Search from './operators/Search';
import { NotAnswered } from './renderers/GridUserRenderer/styles';

// right now we need to maintain the renderers and formatter for dropdowns implemented for the entities
// and dropdowns implemented for the incident json schema
// as soon as we migrate all incidents to events we can remove the renderers and formatters for the incident json schema
const singleChoiceRenderer = ({ value }: { value: JSONSchemaValue }): React.ReactNode => {
  if (!value?.value) {
    return <Text>N/A</Text>;
  }

  // old single choice
  if (typeof value.value === 'string') {
    return <Text>{value.value}</Text>;
  }

  if (Array.isArray(value.value)) {
    return <Text>{value.value.join(', ')}</Text>;
  }

  // new single choice
  if (value.value && 'value' in value.value) {
    const val = value.value.value as { key: string; value: string }[];
    return <Text>{val ? val.map((v) => v.value).join(', ') : 'N/A'}</Text>;
  }

  return <Text>N/A</Text>;
};

const multipleChoiceRenderer = ({ value }: { value: JSONSchemaValue }): React.ReactNode => {
  if (!value?.value) {
    return <Text>N/A</Text>;
  }

  if (typeof value.value === 'string') {
    return <Text>{value.value}</Text>;
  }

  if (Array.isArray(value.value)) {
    return <Text>{value.value.join(', ')}</Text>;
  }

  if ('value' in value.value) {
    const val = value.value.value as { key: string; value: string }[];
    return <Text>{val ? val.map((v) => v.value).join(', ') : 'N/A'}</Text>;
  }

  return <Text>N/A</Text>;
};

export const getFilteringOperator = (protectorType: ProtectorType, momentDate = false): string => {
  switch (protectorType) {
    case ProtectorType.User:
    case ProtectorType.Lookup:
    case ProtectorType.Pipeline:
      return '$>';
    case ProtectorType.Stage:
      return '%>';
    case ProtectorType.Date:
      return momentDate && timezoneStore.enabled ? '=>' : '#>';
    case ProtectorType.DateTime:
      return timezoneStore.enabled ? '=>' : '#>';
    case ProtectorType.Time:
      return '@>';
    case ProtectorType.Location:
      return '&>';
    case ProtectorType.SingleChoice:
    case ProtectorType.MultiChoice:
      return '*>';
    default:
      return '>>';
  }
};

export const getSortingOperator = (protectorType: ProtectorType, momentDate = false): string => {
  switch (protectorType) {
    case ProtectorType.User:
    case ProtectorType.Lookup:
    case ProtectorType.Pipeline:
      return '%>';
    case ProtectorType.Date:
      return momentDate && timezoneStore.enabled ? '@>' : '#>';
    case ProtectorType.DateTime:
      return '@>';
    case ProtectorType.Location:
      return '&>';
    case ProtectorType.SingleChoice:
    case ProtectorType.MultiChoice:
      return '*>';
    case ProtectorType.Stage:
      return '!>';
    case ProtectorType.RiskFactor:
      // The value is a jsonb object, so the default CONTAINS operator reads $.value and
      // annotates NULL on every row. '~>' extracts $.value.score as an integer.
      return '~>';
    case ProtectorType.Number:
      // The default CONTAINS operator reads $.value as text, which ranks 1, 10, 100, 11, 2.
      // '^>' reads it as a number. Filtering deliberately stays on CONTAINS.
      return '^>';
    default:
      return '>>';
  }
};

const formatDateCell = (value: unknown): string => formatFloating(value as DateTimeValue, 'date');
const formatDateTimeCell = (value: unknown): string =>
  formatMoment(value as DateTimeValue, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR);
const formatTimeCell = (value: unknown): string => formatFloating(value as DateTimeValue, 'time');

export const defaultRenderers: {
  [key in GridProtectorType]: (item: {
    value: JSONSchemaValue;
    colDef: { schema: JSONSchemaType };
    hasFocus?: boolean;
  }) => React.ReactNode;
} = {
  [ProtectorType.Date]: ({ value }) => <Text>{(value?.value && formatDateCell(value.value)) || 'N/A'}</Text>,
  [ProtectorType.SingleChoice]: ({ value }) => singleChoiceRenderer({ value }),
  [ProtectorType.YesNoNa]: ({ value }) => singleChoiceRenderer({ value }),
  [ProtectorType.MultiChoice]: ({ value }) => multipleChoiceRenderer({ value }),
  [ProtectorType.LongText]: ({ value }) => <Text>{value?.value ? (value?.value as string) : 'N/A'}</Text>,
  [ProtectorType.ShortText]: ({ value }) => <Text>{value?.value ? (value?.value as string) : 'N/A'}</Text>,
  [ProtectorType.User]: ({ value }) => <GridUserRenderer value={value?.value as string} />,
  [ProtectorType.Location]: ({ value }) => (
    <Text>{(value?.value as unknown as LocationAnswerType)?.address || 'N/A'}</Text>
  ),
  [ProtectorType.Number]: ({ value }) => <Text>{value.value ? (value.value as string) : 'N/A'}</Text>,
  [ProtectorType.DateTime]: ({ value }) => <Text>{(value?.value && formatDateTimeCell(value.value)) || 'N/A'}</Text>,
  [ProtectorType.Time]: ({ value }) => <Text>{(value?.value && formatTimeCell(value.value)) || 'N/A'}</Text>,
  [ProtectorType.Lookup]: ({ value, hasFocus }) => {
    const val = value.value as { name: string; uuid: string; lookup: EntityModules };
    return val.name ? (
      <GridLookupRenderer
        entity={val.lookup}
        instance={{
          uuid: val.uuid,
          name: val.name,
        }}
        hasFocus={hasFocus}
      />
    ) : (
      <NotAnswered>N/A</NotAnswered>
    );
  },
  [ProtectorType.RiskFactor]: ({ value }) => {
    const rating = value?.value as unknown as RiskMatrixValue | null;
    return isRated(rating) ? <RiskMatrixDisplay value={rating} dense /> : <NotAnswered>N/A</NotAnswered>;
  },
  // @ts-ignore
  [ProtectorType.Pipeline]: ({ value }: { value: { schema: JSONSchemaType; value: JSONSchemaValue } }) => {
    const val = value.value?.value as { name?: string; uuid?: string } | null;

    if (!val?.name) {
      return <NotAnswered>N/A</NotAnswered>;
    }

    return <Text>{val.name}</Text>;
  },

  // @ts-ignore
  [ProtectorType.Stage]: ({ value }: { value: { schema: JSONSchemaType; value: JSONSchemaValue } }) => {
    const val = value.value?.value as { name?: string; uuid?: string } | null;

    if (!val?.name) {
      return <NotAnswered>N/A</NotAnswered>;
    }

    return (
      <Chip
        label={val.name}
        colors={{
          background: colors.common.white,
        }}
        sx={{
          border: `1px solid ${colors.grey[400]}`,
        }}
      />
    );
  },
};

// right now we need to maintain the renderers and formatter for dropdowns implemented for the entities
// and dropdowns implemented for the incident json schema
// as soon as we migrate all incidents to events we can remove the renderers and formatters for the incident json schema
const singleChoiceFormatter = ({ value }: { value: JSONSchemaValue }): string => {
  if (value?.value && typeof value?.value === 'string') {
    return value?.value;
  }
  if (value?.value && Array.isArray(value?.value)) {
    return (
      (value?.value && Array.isArray(value?.value) ? value?.value : ([value?.value] as unknown as string[])) || []
    ).join(', ');
  }
  if (value?.value && typeof value.value === 'object' && 'schema' in value.value && 'value' in value.value) {
    const val = value.value.value as { key: string; value: string }[];
    return val ? val.map((v) => v.value).join(', ') : '';
  }
  return value?.value as string;
};

const multipleChoiceFormatter = ({ value }: { value: JSONSchemaValue }): string => {
  if (value?.value && Array.isArray(value?.value)) {
    return (
      (value?.value && Array.isArray(value?.value) ? value?.value : ([value?.value] as unknown as string[])) || []
    ).join(', ');
  }
  if (value?.value && typeof value.value === 'object' && 'schema' in value.value && 'value' in value.value) {
    const val = value.value.value as { key: string; value: string }[];
    return val ? val.map((v) => v.value).join(', ') : '';
  }
  return value?.value as string;
};

export const defaultFormatters: {
  [key in GridProtectorType]: (item: { value: JSONSchemaValue } | null) => string | number;
} = {
  [ProtectorType.Date]: (item: { value: JSONSchemaValue } | string | null) => {
    if (!item) return '';
    // Handle both wrapped object { value: ... } and direct string value (from MUI v7)
    if (typeof item === 'string') {
      return formatDateCell(item);
    }
    const { value } = item;
    return value?.value ? formatDateCell(value.value) : '';
  },
  [ProtectorType.SingleChoice]: (item) => {
    if (!item) return '';
    return singleChoiceFormatter(item);
  },
  [ProtectorType.YesNoNa]: (item) => {
    if (!item) return '';
    return singleChoiceFormatter(item);
  },
  [ProtectorType.MultiChoice]: (item) => {
    if (!item) return '';
    return multipleChoiceFormatter(item);
  },
  [ProtectorType.LongText]: (item) => {
    if (!item) return '';
    return (item.value?.value as string) || '';
  },
  [ProtectorType.ShortText]: (item) => {
    if (!item) return '';
    return (item.value?.value as string) || '';
  },
  [ProtectorType.User]: (item) => {
    if (!item) return '';
    const { value } = item;
    return (value?.value as UuidableName)?.name || (value?.value as string) || '';
  },
  [ProtectorType.Location]: (item) => {
    if (!item) return 'N/A';
    const { value } = item;
    return (value?.value as unknown as LocationAnswerType)?.address || 'N/A';
  },
  [ProtectorType.Number]: (item) => {
    if (!item) return '';
    return (item.value?.value as string) || '';
  },
  [ProtectorType.DateTime]: (item: { value: JSONSchemaValue } | string | null) => {
    if (!item) return '';
    // Handle both wrapped object { value: ... } and direct string value (from MUI v7)
    if (typeof item === 'string') {
      return formatDateTimeCell(item);
    }
    const { value } = item;
    return value?.value ? formatDateTimeCell(value.value) : '';
  },
  [ProtectorType.Time]: (item: { value: JSONSchemaValue } | string | null) => {
    if (!item) return '';
    // Handle both wrapped object { value: ... } and direct string value (from MUI v7)
    if (typeof item === 'string') {
      return formatTimeCell(item);
    }
    const { value } = item;
    return value?.value ? formatTimeCell(value.value) : '';
  },
  [ProtectorType.Lookup]: (item) => {
    if (!item) return '';
    const { value } = item;
    return (value?.value as UuidableName)?.name || (value?.value as string) || '';
  },
  [ProtectorType.RiskFactor]: (item) => {
    if (!item) return '';
    const rating = item.value?.value as unknown as RiskMatrixValue | null;
    if (!isRated(rating)) return '';
    // The score alone, matching core-api's export column, so the two routes agree and the
    // column sorts and pivots in a spreadsheet.
    return String(rating.score);
  },
  [ProtectorType.Pipeline]: (item) => {
    if (!item) return '';
    const { value } = item;
    return (value?.value as UuidableName)?.name || (value?.value as string) || '';
  },
  [ProtectorType.Stage]: (item) => {
    if (!item) return '';
    const { value } = item;
    return (value?.value as UuidableName)?.name || (value?.value as string) || '';
  },
  [ProtectorType.Attachment]: (item) => {
    if (!item) return '';
    const { value } = item;
    return (value?.value as UuidableName)?.name || (value?.value as string) || '';
  },
  [ProtectorType.Signature]: () => '',
  [ProtectorType.RepeatingGroup]: () => '',
};

export const defaultOperators: {
  [key in GridProtectorType]: (props?: { schema?: Partial<JSONSchemaType> }) => GridFilterOperator[];
} = {
  [ProtectorType.Date]: ExtendedDateOperators,
  [ProtectorType.SingleChoice]: textOperators,
  [ProtectorType.YesNoNa]: textOperators,
  [ProtectorType.MultiChoice]: textOperators,
  [ProtectorType.LongText]: textOperators,
  [ProtectorType.ShortText]: textOperators,
  [ProtectorType.User]: userOperators,
  [ProtectorType.Location]: textOperators,
  [ProtectorType.Number]: textOperators,
  [ProtectorType.DateTime]: BasicDateOperators,
  [ProtectorType.Time]: BasicDateOperators,
  [ProtectorType.Lookup]: (props) =>
    Search(
      (params) =>
        API.entities.searchEntity(props?.schema?.properties?.lookupType as unknown as string, undefined, {
          ...params,
          limit: 20,
        }),
      (uuid: string) =>
        API.entities.loadEntitiesDetails(props?.schema?.properties?.lookupType as unknown as string, uuid),
      'name'
    ),
  // No filter UI. The backend matches a rating by exact score only, which none of the text
  // operators can express, and offering them filters every row away.
  [ProtectorType.RiskFactor]: (): GridFilterOperator[] => [],
  [ProtectorType.Pipeline]: textOperators,
  [ProtectorType.Stage]: textOperators,
  [ProtectorType.Attachment]: textOperators,
  [ProtectorType.Signature]: textOperators,
  [ProtectorType.RepeatingGroup]: textOperators,
};

export const defaultColumnProps = {
  aggregable: false,
  flex: 1,
  groupable: false,
  filterOperators: defaultOperators[ProtectorType.ShortText](),
  sortable: true,
  // MUI v8 passes the raw cell value; columns with a renderCell get it wrapped as { value }
  // by their valueGetter. Only missing values and false render blank, so 0 still shows.
  valueFormatter: (value: unknown) => {
    const raw =
      value !== null && typeof value === 'object' && 'value' in value ? (value as { value: unknown }).value : value;
    if (raw === null || raw === undefined || raw === false) return '';
    return raw;
  },
};

export const getFilters = (items: FilterType[]): StringKeys => {
  const groupedFilters = items
    .filter((item) => item.field && item.value)
    .reduce(
      (accumulator, { field, value }) => ({
        ...accumulator,
        [field]: [...(accumulator[field] || []), Array.isArray(value) ? value.join('::') : value],
      }),
      {} as { [key: string]: string[] }
    );
  return Object.fromEntries(Object.entries(groupedFilters).map(([key, value]) => [key, value.join(';;')]));
};

export const getFilterOperators = (
  protectorType: ProtectorType,
  isEntityList: boolean,
  lookupType?: JSONSchemaType
): GridFilterOperator[] => {
  const operators = defaultOperators[protectorType as GridProtectorType];

  if (
    isEntityList &&
    (protectorType === ProtectorType.Date ||
      protectorType === ProtectorType.DateTime ||
      protectorType === ProtectorType.Time)
  ) {
    return RangeOperators();
  }

  if (protectorType === ProtectorType.Date) {
    return defaultOperators[ProtectorType.DateTime]();
  }

  if (!operators) {
    return defaultOperators[ProtectorType.ShortText]();
  }

  return operators({
    schema: lookupType ? { properties: { lookupType } } : undefined,
  });
};
